import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { usePro } from '../contexts/ProContext';
import LessonBoard from '../hood-training/LessonBoard';
import { chapters, lessons, parsePosition, legalMoves, playMove, goalReached, invalidMoveMessage, resolveQuestion, readProgress, colour, names } from '../hood-training/trainingModel';
import { askBeginnerCoach } from '../hood-training/coachApi';
import '../hood-training/training.css';
import HoodVoiceControls from '../components/HoodVoiceControls';
import HoodLevelPicker from '../components/HoodLevelPicker';
import HoodCareerPath from '../components/HoodCareerPath';
import { getWebHoodVoiceEnabled, speakWebHoodVoice, stopWebHoodVoice } from '../utils/hoodWebSpeech';
import { trackUsageEvent } from '../analytics/UsageAnalytics';

export default function HoodBeginnerTraining({
  guidedJourney = false,
  returnTo = "/hood-ai-alpha",
  variantLabel = "ALPHA",
  onChangeLevel,
  showLevelControl = false,
  currentLevel = 0,
  accountAgeDays = 0,
  onAccountAgeChange,
}) {
  const {firebaseUser,loading}=useContext(UserContext);
  const {isPro,refresh}=usePro();const location=useLocation();
  const key=`${guidedJourney?'hood:zero-journey:v1':'hood:first-moves:v1'}:${firebaseUser?.uid||'guest'}`;
  const [index,setIndex]=useState(0),[completed,setCompleted]=useState([]),[loadedKey,setLoadedKey]=useState('');
  const [board,setBoard]=useState(()=>parsePosition(lessons[0].position));
  const [selected,setSelected]=useState(null),[showHints,setShowHints]=useState(false),[done,setDone]=useState(false);
  const [speech,setSpeech]=useState(guidedJourney
    ? "Welcome. I’ll teach you chess from the very beginning. We’ll go one small step at a time, and I won’t move on until you’ve tried it yourself. First, meet the rook."
    : lessons[0].speech),[question,setQuestion]=useState(''),[lastQuestion,setLastQuestion]=useState(''),[lastAnswer,setLastAnswer]=useState('');
  const [thinking,setThinking]=useState(false),[assist,setAssist]=useState(''),[menu,setMenu]=useState(false),[promotion,setPromotion]=useState(null);
  const [playedFen,setPlayedFen]=useState('');
  const [returnPoint,setReturnPoint]=useState(null),[sound,setSound]=useState(()=>getWebHoodVoiceEnabled()),[billingMessage,setBillingMessage]=useState('');
  const [speechRevision,setSpeechRevision]=useState(0);
  const request=useRef(null),revision=useRef(0),root=useRef(null),topicButton=useRef(null),dialogRef=useRef(null);
  const lesson=lessons[index];
  const announce=(text)=>{
    setSpeech(text);
    setSpeechRevision(value=>value+1);
  };
  const answerUser=(text)=>{
    const clean=String(text||'').trim();
    if(!clean)return;
    setLastAnswer(clean);
    announce(clean);
  };
  const cancelRequest=()=>{revision.current++;request.current?.abort();request.current=null;setThinking(false);};
  const resetLesson=(next,asked='')=>{
    cancelRequest();const item=lessons[next];setIndex(next);setBoard(parsePosition(item.position));setSelected(null);setDone(false);setPromotion(null);setPlayedFen('');setAssist('');setShowHints(Boolean(asked));setLastQuestion(asked);setLastAnswer('');announce(item.speech);
    if(window.speechSynthesis)window.speechSynthesis.cancel();
  };
  useEffect(()=>{
    if(loading)return;
    let saved={index:0,completed:[]};
    try{saved=readProgress(window.localStorage, firebaseUser && !window.localStorage.getItem(key) ? "hood:first-moves:v1:guest" : key);}catch{}
    setIndex(saved.index);setCompleted(saved.completed);setLoadedKey(key);
    const item=lessons[saved.index];setBoard(parsePosition(item.position));announce(guidedJourney&&saved.index===0?"Welcome. I’ll teach you chess from the very beginning. We’ll go one small step at a time, and I won’t move on until you’ve tried it yourself. First, meet the rook.":item.speech);setSelected(null);setDone(false);setShowHints(false);setLastQuestion('');setReturnPoint(null);setPlayedFen('');setAssist('');
    revision.current++;request.current?.abort();setThinking(false);
  },[guidedJourney,key,loading]);
  useEffect(()=>{
    if(loadedKey!==key||loading)return;
    try{window.localStorage.setItem(key,JSON.stringify({version:1,index,completed}));}catch{}
  },[index,completed,key,loadedKey,loading]);
  useEffect(()=>{
    document.documentElement.classList.add('hood-learning-active');
    return()=>document.documentElement.classList.remove('hood-learning-active');
  },[]);
  useEffect(()=>{
    const v=window.visualViewport;
    const resize=()=>{if(root.current)root.current.style.setProperty('--hood-height',`${v?.height||window.innerHeight}px`);};
    resize();v?.addEventListener('resize',resize);window.addEventListener('resize',resize);
    return()=>{v?.removeEventListener('resize',resize);window.removeEventListener('resize',resize);request.current?.abort();window.speechSynthesis?.cancel();};
  },[]);
  useEffect(()=>{
    if(!sound||!speech)return;
    speakWebHoodVoice(speech,'en');
    return()=>stopWebHoodVoice();
  },[speech,sound,speechRevision]);
  useEffect(()=>{
    if(!menu&&!promotion)return;
    const prior=document.activeElement;
    dialogRef.current?.querySelector('button')?.focus();
    const handle=event=>{
      if(event.key==='Escape'){setMenu(false);setPromotion(null);}
      if(event.key==='Tab'){
        const nodes=[...(dialogRef.current?.querySelectorAll('button,a[href]')||[])];
        const first=nodes[0],last=nodes[nodes.length-1];
        if(event.shiftKey&&document.activeElement===first){event.preventDefault();last?.focus();}
        if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first?.focus();}
      }
    };
    document.addEventListener('keydown',handle);
    return()=>{document.removeEventListener('keydown',handle);prior?.focus?.();};
  },[menu,promotion]);
  useEffect(()=>{
    const result=new URLSearchParams(location.search).get('pro');
    if(result==='cancelled'){setBillingMessage('Checkout closed. You can keep learning.');return;}
    if(result!=='return'||!firebaseUser)return;
    let active=true;
    setBillingMessage('Checking your Pro access…');
    refresh().then(()=>{if(active)setBillingMessage('Your account has been refreshed. If Pro is still pending, use Check Pro again.');}).catch(()=>{if(active)setBillingMessage('We could not check Pro yet. Please try Check Pro again.');});
    return()=>{active=false;};
  },[location.search,firebaseUser,refresh]);
  useEffect(()=>{if(isPro)setBillingMessage('Pro is active. Welcome back!');},[isPro]);
  const destinations=useMemo(()=>selected&&!done?legalMoves(lesson,board,selected).map(m=>m.to):[],[selected,done,lesson,board]);
  const arrows=showHints&&!done?lesson.arrows:[];
  const targets=showHints&&!done&&Array.isArray(lesson.goal)?lesson.goal.map(m=>m.slice(2,4)):[];
  const markComplete=()=>setCompleted(current=>{
    if(current.includes(lesson.id))return current;
    trackUsageEvent('lesson_complete',{
      meta:{
        lesson:lesson.id,
        category:lesson.chapter||lesson.chapterId||'beginner',
        variant:String(variantLabel||'alpha').toLowerCase(),
      },
    });
    return [...current,lesson.id];
  });
  function move(from,to,promote='q'){
    cancelRequest();
    const played=playMove(lesson,board,from,to,promote);
    if(!played){announce(invalidMoveMessage(lesson,board,from,to));setShowHints(true);return;}
    if(!goalReached(lesson,from,to)&&lesson.goal!==null&&lesson.goal!=='tap'){
      announce(`That move is allowed. For this exercise, ${lesson.task.toLowerCase()}. Try the highlighted target.`);setShowHints(true);return;
    }
    setBoard(played.board);setPlayedFen(played.move.after||'');setSelected(null);setPromotion(null);setDone(true);markComplete();
    const pieceName=names[board[from].toLowerCase()];
    announce(played.move.promotion?`You promoted your pawn to a ${names[promote]}! Well done.`:played.move.flags?.includes('k')||played.move.flags?.includes('q')?'You castled! Your king and rook moved together in one turn.':played.move.captured?`Yes! Your ${pieceName} captured the ${names[played.move.captured]}. The captured piece comes off the board.`:`Well done! Your ${pieceName} moved from ${from} to ${to}. You did it yourself.`);
  }
  function tap(square){
    if(done||lesson.goal===null)return;
    if(selected&&destinations.includes(square)){
      const piece=board[selected];
      if(piece?.toLowerCase()==='p'&&/[18]$/.test(square)){setPromotion({from:selected,to:square});return;}
      move(selected,square);return;
    }
    if(selected&&square!==selected&&(!board[square]||colour(board[square])!==lesson.turn)){move(selected,square);return;}
    if(board[square]&&colour(board[square])===lesson.turn){
      setSelected(square===selected?null:square);
      if(lesson.goal==='tap'){markComplete();announce('Great! The dots show where your piece can go. Tap one to try a move, or continue to the next example.');}
    }else announce('Tap your piece first. Then tap a highlighted square to move it.');
  }
  const canContinue=done||lesson.goal===null||(lesson.goal==='tap'&&!!selected);
  function next(){
    if(!canContinue){announce('Let’s try the move together first. Tap your piece, then the highlighted square.');setShowHints(true);return;}
    markComplete();
    if(index<lessons.length-1){
      const nextLesson=lessons[index+1];
      const chapterChanged=nextLesson.chapterId!==lesson.chapterId;
      resetLesson(index+1);
      setReturnPoint(null);
      if(guidedJourney&&chapterChanged){
        announce(`Good. You finished ${lesson.chapter}. Now I’ll teach you ${nextLesson.chapter.toLowerCase()}. ${nextLesson.speech}`);
      }
    }else{
      setDone(true);
      announce(guidedJourney
        ? 'You have completed your full beginner journey. You now know every piece, captures, check, checkmate and the special moves. Your next step is your first complete game with Hood beside you.'
        : 'You have met every piece, checkmate and the special moves. Brilliant work! You can revisit a lesson or try a game with Hood.');
    }
  }
  async function ask(event){
    event.preventDefault();const text=question.trim();if(!text||thinking)return;
    cancelRequest();setQuestion('');setAssist('');setLastAnswer('');
    const found=resolveQuestion(text,lesson);
    if(found>=0){if(returnPoint===null)setReturnPoint(index);resetLesson(found,text);return;}
    if(/^(help|show me|hint|again|i dont understand)[.!? ]*$/i.test(text.replace(/[’']/g,''))){setShowHints(true);setLastQuestion(text);answerUser(lesson.speech);return;}
    setLastQuestion(text);const controller=new AbortController();request.current=controller;const version=revision.current;setThinking(true);
    const timer=setTimeout(()=>controller.abort(),25000);
    try{
      const answer=await askBeginnerCoach({user:firebaseUser,question:text,lesson,board,fen:playedFen,signal:controller.signal});
      if(version!==revision.current)return;
      if(answer.needsLogin){setAssist('login');answerUser('Sign in to ask Hood your own questions. You can still try every lesson and ask me how each piece moves.');}
      else if(answer.needsPro){setAssist('pro');answerUser('Your AI coaching allowance has ended. Continue with Pro, or keep practising these lessons.');}
      else answerUser(answer.speech);
    }catch(error){if(version===revision.current)answerUser(error.name==='AbortError'?'Hood took too long to answer. Try again — your lesson is still here.':error.message);}
    finally{clearTimeout(timer);if(version===revision.current)setThinking(false);}
  }
  const proLink='/checkout?returnTo=hoodai';
  const progressPercent=Math.round(((index+(done?1:0))/lessons.length)*100);
  return <main className="hood-learn" ref={root} lang="en">
    <div className="hood-lesson-shell">
      <header className="hood-header"><Link to="/" aria-label="Back to Chesshood">‹</Link><span>HOOD <b>AI</b> <small style={{fontSize:9,color:guidedJourney?"#7dd3fc":"#d9cf93"}}>{guidedJourney?`${variantLabel} · BEGINNER JOURNEY`:variantLabel}</small></span><div className="hood-header-right">{showLevelControl&&<HoodLevelPicker
          lang="en"
          currentElo={currentLevel}
          compact
          allowBeginner
          ownerQa
          accountAgeDays={accountAgeDays}
          onAccountAgeChange={onAccountAgeChange}
          onSelect={elo=>onChangeLevel?.(elo)}
        />}<HoodVoiceControls lang="en" compact enabled={sound} onEnabledChange={setSound} /><Link className="hood-pro" to={isPro?'/profile':proLink}>{isPro?'Pro ✓':'Go Pro'}</Link></div></header>
      {guidedJourney&&<div style={{padding:"8px 14px 0"}}><div style={{height:7,borderRadius:999,background:"rgba(255,255,255,.12)",overflow:"hidden"}}><div style={{height:"100%",width:`${progressPercent}%`,background:"#7dd3fc",transition:"width .25s ease"}}/></div><div style={{marginTop:5,color:"#9ec4df",fontSize:11,fontWeight:800}}>{index+1} / {lessons.length} · {progressPercent}% complete</div></div>}
      {guidedJourney&&<div style={{padding:"6px 14px 2px"}}>
        <HoodCareerPath
          rating={currentLevel}
          lang="en"
          accountAgeDays={showLevelControl ? accountAgeDays : null}
          qa={showLevelControl}
          userId={firebaseUser?.uid || "guest"}
          compact
        />
      </div>}
      <div className="hood-lesson-title"><div><span>{guidedJourney?'BEGINNER JOURNEY':'FIRST MOVES'} · {lesson.chapter}</span><h1>{lesson.title}</h1></div>{!guidedJourney&&<button type="button" ref={topicButton} onClick={()=>setMenu(true)} aria-label="Choose a lesson">{index+1}/{lessons.length} ☰</button>}</div>
      <section className="hood-coach" aria-label="Hood’s explanation"><img src="/hood.png" alt="Hood, your chess coach" onError={e=>{e.currentTarget.style.visibility='hidden';}}/><div
        className="hood-bubble"
        role="button"
        tabIndex={0}
        title={sound ? "Tap to read this again" : "Turn voice on to read aloud"}
        onClick={() => sound && speech && speakWebHoodVoice(speech,'en')}
        onKeyDown={event => {
          if (!sound || !speech) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            speakWebHoodVoice(speech,'en');
          }
        }}
        style={{cursor:sound?'pointer':'default',WebkitTapHighlightColor:'rgba(125,211,252,.14)'}}
      ><strong>HOOD</strong><p aria-live="polite">{thinking?'Let me think about your question…':speech}</p>{assist==='login'&&<Link to="/login" state={{from:{pathname:'/hoodai'}}}>Sign in to ask Hood</Link>}{assist==='pro'&&<Link to={proLink}>Continue with Pro</Link>}{billingMessage&&<small>{billingMessage} {!isPro&&<button type="button" onClick={()=>refresh().catch(()=>setBillingMessage('Please try again in a moment.'))}>Check Pro</button>}</small>}</div></section>
      <LessonBoard board={board} lesson={lesson} selected={selected} arrows={arrows} targets={targets} onSquare={tap} onMove={(from,to)=>{if(board[from]?.toLowerCase()==='p'&&/[18]$/.test(to))setPromotion({from,to});else move(from,to);}} disabled={done||lesson.goal===null||!!promotion}/>
      <section className="hood-task" aria-label="Your task"><span>{done?'✓ YOU DID IT':lesson.goal===null?'LOOK TOGETHER':`YOUR TURN · ${lesson.turn==='w'?'WHITE':'BLACK'}`}</span><p>{done?'Ready for the next step?':lesson.task}</p></section>
      <nav className="hood-controls" aria-label="Lesson controls"><button type="button" onClick={()=>{resetLesson(index);setShowHints(true);}}>Show me</button><button type="button" aria-label="Restart this lesson" onClick={()=>resetLesson(index)}>↺</button><button type="button" className="hood-primary" onClick={next}>{index===lessons.length-1?'Finish':canContinue?'Continue →':'Try the move'}</button></nav>
      {returnPoint!==null&&<button className="hood-return" type="button" onClick={()=>{resetLesson(returnPoint);setReturnPoint(null);}}>← Back to my lesson</button>}
      <form className="hood-chat" onSubmit={ask}>
        <label className="hood-sr-only" htmlFor="hood-question">Ask Hood a question</label>
        {lastQuestion&&<div className="hood-last-question" title={lastQuestion}><b>YOU</b> {lastQuestion}</div>}
        {(thinking||lastAnswer)&&<div
          className="hood-inline-answer"
          role="button"
          tabIndex={lastAnswer?0:-1}
          onClick={()=>lastAnswer&&sound&&speakWebHoodVoice(lastAnswer,'en')}
          onKeyDown={event=>{
            if(!lastAnswer||!sound)return;
            if(event.key==='Enter'||event.key===' '){event.preventDefault();speakWebHoodVoice(lastAnswer,'en');}
          }}
          title={lastAnswer&&sound?'Tap to hear Hood again':''}
        ><b>HOOD</b> {thinking?'Thinking…':lastAnswer}</div>}
        <div><input id="hood-question" value={question} maxLength={600} onChange={e=>setQuestion(e.target.value)} placeholder="Ask Hood: how does a rook move?" autoComplete="off"/><button type="submit" disabled={!question.trim()||thinking} aria-label="Send question">↑</button></div>
      </form>
      <footer className="hood-footer"><span>{completed.length} steps explored</span><Link to={returnTo}>{guidedJourney&&index===lessons.length-1&&done?'Start my first full game with Hood →':'Play & analyse with Hood →'}</Link></footer>
    </div>
    {((menu&&!guidedJourney)||promotion)&&<div className="hood-modal-backdrop" onClick={()=>{setMenu(false);setPromotion(null);}}><section ref={dialogRef} className="hood-dialog" role="dialog" aria-modal="true" aria-label={promotion?'Choose your new piece':'Choose a lesson'} onClick={e=>e.stopPropagation()}><button className="hood-close" type="button" onClick={()=>{setMenu(false);setPromotion(null);}} aria-label="Close">×</button><h2>{promotion?'Choose your new piece':'Your first moves'}</h2>{promotion?<div className="hood-promotions">{['q','r','b','n'].map(p=><button key={p} type="button" onClick={()=>move(promotion.from,promotion.to,p)}>{names[p]}</button>)}</div>:<>{chapters.map(chapter=><details key={chapter.id} open={chapter.id===lesson.chapterId}><summary>{chapter.name}</summary>{chapter.lessons.map(item=><button type="button" className="hood-topic" key={item.id} onClick={()=>{resetLesson(lessons.findIndex(l=>l.id===item.id));setMenu(false);setReturnPoint(null);}}>{completed.includes(item.id)?'✓ ':''}{item.title}</button>)}</details>)}</>}</section></div>}
  </main>;
}

